import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  InputAdornment,
  Typography,
} from '@mui/material';

import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CustomSelect from '../../../../components/CustomSelect';
import InputField from '../../../../shared/ui/elements/Input';
import CloseIcon from '../../../digital-account/ui/components/close-icon';
import { AddEmployeeSchema } from './model/schema';
import { EmployeeFormFields } from './model/types';

const AddEmployeeModal = (props: {
  closeModal: () => void;
  onSubmit: (data: EmployeeFormFields, idx?: number | null) => void;
  idx?: number | null;
  dialogProps?: DialogProps;
}) => {
  const { closeModal, onSubmit, idx, dialogProps } = props;
  const { t } = useTranslation('salary-card');

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useFormContext<AddEmployeeSchema>();

  return (
    <Dialog {...dialogProps} onClose={closeModal}>
      <DialogTitle fontSize={28} fontWeight={600}>
        {t('addEmployee.title')}
      </DialogTitle>

      <DialogContent>
        <CloseIcon close={closeModal} />

        <form onSubmit={handleSubmit(data => onSubmit(data, idx))}>
          <Box display='flex' flexDirection='column' gap={3}>
            {/* Full name */}
            <Controller
              control={control}
              name='fullName'
              render={({ field }) => (
                <InputField
                  labelText={t('addEmployee.fields.fullName.label')}
                  placeholder={t('addEmployee.fields.fullName.placeholder')}
                  error={!!errors?.fullName}
                  errorText={errors?.fullName?.message}
                  {...field}
                />
              )}
            />

            {/* FIN */}
            <Controller
              control={control}
              name='pin'
              render={({ field }) => (
                <InputField
                  labelText={t('addEmployee.fields.pin.label')}
                  placeholder={t('addEmployee.fields.pin.placeholder')}
                  maskOptions={{
                    mask: /^[A-NP-Za-np-z0-9]{1,7}$/,
                  }}
                  error={!!errors?.pin}
                  errorText={errors?.pin?.message}
                  {...field}
                  onChange={e => field.onChange(e.target.value.toUpperCase())}
                />
              )}
            />

            {/* Passport */}
            <Box>
              <Typography fontSize={15}>{t('addEmployee.fields.serial.label')}</Typography>

              <Box display='flex' gap={2}>
                <CustomSelect
                  width='25%'
                  placeholder='AA'
                  options={[
                    { value: 'AA', label: 'AA' },
                    { value: 'AZE', label: 'AZE' },
                    { value: 'AB', label: 'AB' },
                  ]}
                  control={control}
                  rest={{ ...register('serialType') }}
                />

                <Controller
                  control={control}
                  name='serialNumber'
                  render={({ field }) => (
                    <InputField
                      labelText=''
                      placeholder={t('addEmployee.fields.serial.placeholder')}
                      maskOptions={{
                        mask: /^[0-9]{0,8}$/,
                      }}
                      error={!!errors?.serialNumber}
                      errorText={errors?.serialNumber?.message}
                      {...field}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Phone */}
            <Controller
              control={control}
              name='phone'
              render={({ field }) => (
                <InputField
                  labelText={t('addEmployee.fields.phone.label')}
                  placeholder='(00) 000 00 00'
                  maskOptions={{ mask: '00 000 00 00' }}
                  sx={{ '& .MuiInputBase-input': { padding: 0 } }}
                  startAdornment={
                    <InputAdornment position='start'>
                      <Typography>+994</Typography>
                    </InputAdornment>
                  }
                  error={!!errors?.phone}
                  errorText={errors?.phone?.message}
                  {...field}
                />
              )}
            />

            {/* Position */}
            <Controller
              control={control}
              name='position'
              render={({ field }) => (
                <InputField
                  labelText={t('addEmployee.fields.position.label')}
                  placeholder={t('addEmployee.fields.position.placeholder')}
                  error={!!errors?.position}
                  errorText={errors?.position?.message}
                  {...field}
                />
              )}
            />
          </Box>

          <Box display='flex' justifyContent='flex-end' mt={4}>
            <Button type='submit'>{t('addEmployee.submit')}</Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmployeeModal;
